import React from 'react';

import FFWebTheme from 'ff/debrief/common/StyledComponentThemes';
import TextField from '@mui/material/TextField';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import { FilterOptionsState } from '@mui/material/useAutocomplete';

import { DebriefAircraft } from 'ff/debrief/models';
import { useGetAircraftListQuery } from 'ff/debrief/features/TracklogApi';
import { useField, useFormikContext } from 'formik';
import { isCustom } from 'ff/debrief/utils';

interface Props {
    value: DebriefAircraft;
    onChange: (newValue: DebriefAircraft) => void;
}

const customOption: DebriefAircraft = {
    manufacturer: 'Custom',
    model: 'Custom',
    label: 'Custom',
};

const AircraftModelField = (props: React.PropsWithChildren<Props>) => {
    const [inputValue, setInputValue] = React.useState('');
    const [, meta] = useField('aircraftModel');
    const { validateField } = useFormikContext();

    const { data: aircraftModels, isLoading } = useGetAircraftListQuery(
        inputValue,
        {
            skip: isCustom(inputValue),
        }
    );

    React.useEffect(() => {
        if (props.value && (!props.value.manufacturer || !props.value.model)) {
            validateField('aircraftModel');
        }
    }, []);

    const hasError = meta.error;

    const getErrorMessage = () => {
        if (!hasError) return '';
        if (typeof meta.error === 'string') return meta.error;
        const errorObj = meta.error as Record<string, string>;
        return errorObj?.manufacturer || errorObj?.model;
    };

    const filter = createFilterOptions<DebriefAircraft>();

    const filterOptions = (
        options: DebriefAircraft[],
        params: FilterOptionsState<DebriefAircraft>
    ) => {
        const filtered = filter(options, params);

        const hasCustomOption = filtered.some(aircraft =>
            isCustom(aircraft.model)
        );

        if (!hasCustomOption) filtered.push(customOption);
        return filtered;
    };

    const handleChange = (
        evt: React.SyntheticEvent,
        newValue: DebriefAircraft
    ) => {
        props.onChange(newValue);
    };

    const handleInputChange = (evt: React.SyntheticEvent, newValue: string) => {
        setInputValue(newValue);
    };

    return (
        <Autocomplete
            sx={{ flexGrow: 1.5 }}
            options={aircraftModels || []}
            value={props.value}
            inputValue={inputValue}
            onChange={handleChange}
            onInputChange={handleInputChange}
            filterOptions={filterOptions}
            loading={isLoading}
            disableClearable
            getOptionLabel={option => {
                if (!option.manufacturer || !option.model) return '';
                if (isCustom(option.model)) return 'Custom';
                return option.label;
            }}
            renderInput={params => (
                <TextField
                    {...params}
                    label="Aircraft"
                    variant="standard"
                    error={!!hasError}
                    helperText={getErrorMessage()}
                    placeholder="Search for your aircraft model"
                    sx={{
                        input: {
                            color: FFWebTheme.blue,
                            fontWeight: 600,
                        },
                    }}
                />
            )}
        />
    );
};

export default AircraftModelField;
